// Axios wrappers for the per-workspace proposal export templates API
// (backend: organization/views_templates.py). All calls are org-scoped via the
// selected_org_id cookie, mirroring StatusConfiguration's data layer.
import Cookies from "js-cookie";
import api from "../pages/chat/api";

const BASE = "/api/v1/org/proposal-templates";

const orgId = () => Cookies.get("selected_org_id");
const withOrg = (path) => {
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}organization_id=${orgId()}`;
};

// Client mirror of the backend BUILTIN_DEFAULT_CONFIG (rfp/services/
// proposal_template_config.py). Used as the starting point for a new template.
export const DEFAULT_TEMPLATE_CONFIG = {
  theme: "builtin_default",
  logo: { url: "", position: "top_left", cover: { x: 0.07, y: 0.06, width: 0.18 }, header: { x: 0.07 } },
  // Colours are per-section so cover and body pages are styled independently.
  cover_page: {
    enabled: true,
    content_layout: "centered",
    background_image_url: "",
    show_page_number: false,
    // Hides the whole cover text layer when the uploaded artwork is the cover.
    artwork_only: false,
    colors: { headings: "#111827", body: "#374151" },
    fields: { title: "", subtitle: "" },
    // Free-form cover lines, in render order: { label, value }. Label optional.
    blocks: [],
  },
  body_pages: {
    header_style: "none",
    page_number: { show: false, position: "bottom_right" },
    page_margins: "large",
    colors: { headings: "#111827", body: "#374151" },
    header_text: "",
    footer_text: "",
  },
};

// Normalize a (possibly legacy-shaped) config to the current schema, filling
// any missing keys and migrating an old top-level `colors` into per-section
// cover/body colours. Keeps the settings UI crash-proof for templates saved
// before colours were split per section.
const LOGO_POS_BOX = {
  top_left: { x: 0.07, y: 0.06, width: 0.18 },
  top_center: { x: 0.41, y: 0.06, width: 0.18 },
  top_right: { x: 0.75, y: 0.06, width: 0.18 },
};

// Mirrors MAX_COVER_BLOCKS in rfp/services/proposal_template_config.py — the
// server truncates past this, so the UI must stop offering more.
export const MAX_COVER_BLOCKS = 12;

// The fixed cover fields that existed before `blocks`, in render order. Mirrors
// _LEGACY_COVER_FIELDS in rfp/services/proposal_template_config.py.
const LEGACY_COVER_FIELDS = [
  ["prepared_for", "Prepared For"],
  ["prepared_by", "Prepared By"],
  ["creation_date", "Creation Date"],
];

// An explicit blocks array always wins — including an empty one, which means the
// user deleted every line. Only a config saved before this feature (no `blocks`
// key at all) migrates the three legacy fields into the list.
const coverBlocks = (cover) => {
  if (Array.isArray(cover.blocks)) {
    return cover.blocks
      .filter((b) => b && typeof b === "object")
      .map((b) => ({ label: b.label || "", value: b.value || "" }))
      .filter((b) => b.label || b.value);
  }
  const f = cover.fields || {};
  return LEGACY_COVER_FIELDS
    .filter(([k]) => (f[k] || "").trim())
    .map(([k, label]) => ({ label, value: f[k] }));
};

export const normalizeClientConfig = (raw) => {
  const d = DEFAULT_TEMPLATE_CONFIG;
  const c = raw && typeof raw === "object" ? raw : {};
  const legacy = c.colors || {};
  const coverColors = (c.cover_page && c.cover_page.colors) || {};
  const bodyColors = (c.body_pages && c.body_pages.colors) || {};
  // Logo: cover + header are positioned independently. Explicit sub-boxes win;
  // else fall back to any old single box (cl.x/y/width), then to the box implied
  // by `position`, so older templates still place the logo sensibly.
  const cl = c.logo || {};
  const posBox = LOGO_POS_BOX[cl.position] || LOGO_POS_BOX.top_left;
  const oldX = cl.x != null ? cl.x : posBox.x;
  const oldY = cl.y != null ? cl.y : posBox.y;
  const oldW = cl.width != null ? cl.width : posBox.width;
  const cov = cl.cover || {};
  const hdr = cl.header || {};
  return {
    theme: c.theme || d.theme,
    logo: {
      url: cl.url || "",
      position: cl.position || d.logo.position,
      cover: {
        x: cov.x != null ? cov.x : oldX,
        y: cov.y != null ? cov.y : oldY,
        width: cov.width != null ? cov.width : oldW,
      },
      header: { x: hdr.x != null ? hdr.x : oldX },
    },
    cover_page: {
      ...d.cover_page,
      ...(c.cover_page || {}),
      colors: {
        headings: coverColors.headings || legacy.headings || d.cover_page.colors.headings,
        body: coverColors.body || legacy.body || d.cover_page.colors.body,
      },
      // Only title/subtitle live in `fields` now; the rest are `blocks`, so the
      // legacy keys are deliberately dropped rather than spread through.
      fields: {
        title: ((c.cover_page && c.cover_page.fields) || {}).title || "",
        subtitle: ((c.cover_page && c.cover_page.fields) || {}).subtitle || "",
      },
      blocks: coverBlocks(c.cover_page || {}),
    },
    body_pages: {
      ...d.body_pages,
      ...(c.body_pages || {}),
      colors: {
        headings: bodyColors.headings || legacy.headings || d.body_pages.colors.headings,
        body: bodyColors.body || legacy.body || d.body_pages.colors.body,
      },
      page_number: { ...d.body_pages.page_number, ...((c.body_pages && c.body_pages.page_number) || {}) },
    },
  };
};

export const listTemplates = () =>
  api.get(withOrg(`${BASE}/`)).then((r) => r.data);

export const getTemplate = (id) =>
  api.get(withOrg(`${BASE}/${id}/`)).then((r) => ({
    ...r.data,
    config: normalizeClientConfig(r.data.config),
  }));

export const createTemplate = (payload) =>
  api.post(withOrg(`${BASE}/`), payload).then((r) => r.data);

export const updateTemplate = (id, payload) =>
  api.patch(withOrg(`${BASE}/${id}/`), payload).then((r) => r.data);

export const deleteTemplate = (id) =>
  api.delete(withOrg(`${BASE}/${id}/`)).then((r) => r.data);

export const setDefaultTemplate = (id) =>
  api.post(withOrg(`${BASE}/${id}/set-default/`)).then((r) => r.data);

export const uploadTemplateImage = (file) => {
  const form = new FormData();
  form.append("image", file);
  return api
    .post(withOrg(`${BASE}/upload-image/`), form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data); // { url }
};
